(ns leg.keys
  "Steering this reader from a keyboard.

  There is no test for whether one is attached, because there does not need
  to be: a `keydown` only ever arrives from a keyboard, so a phone with
  nothing plugged into it never reaches this file and a phone with a
  keyboard paired to it does. The screens are the same either way - this is
  a second way to do what a finger already does, not a mode.

  Backspace goes back, on every screen, because that is what it did on the
  web for twenty years and what the hand expects of it (`leg.state/back!`).
  Everything else here is vim's keys, because they are the ones somebody who
  steers anything with a keyboard already has in their hands: `j` and `k`
  down and up - one row in a list, a couple of lines in an article - and `h`
  and `l` one article back and forward. All four can be changed under Settings; the defaults are here
  and the changes are on the server, so the keys somebody chose on the
  laptop are the keys the next laptop has.

  Moving in a list is done by moving the focus, and nothing else. The ring
  is already drawn and themed, the browser scrolls the row into view, a
  screen reader says what the row is, and the return key opens it - all of
  that for free, because a row is a real button. A highlight of this
  window's own would have been four of those things reimplemented, worse."
  (:require [clojure.string :as str]
            [leg.state :as state]))

(def defaults
  {:keys/up "k" :keys/down "j" :keys/previous "h" :keys/next "l"})

(def actions
  "The four, in the order the settings show them, and what to call them
  there."
  [[:keys/up       "Up"]
   [:keys/down     "Down"]
   [:keys/previous "Previous article"]
   [:keys/next     "Next article"]])

(defn bindings
  "What each action is on, for this person. The server only holds what was
  changed, so the defaults are merged under it (see
  `leser.readers/update-settings!`)."
  [settings]
  (merge defaults (:reader/keys settings)))

(def line
  "How far one press moves the page while reading, in pixels. Two lines of
  the body rather than one: a press per line is a reader nobody would use,
  and a screenful is what the space bar is already for."
  72)

;;; ------------------------------------------------------------ what counts

(defn- typing?
  "Whether this key belongs to something that takes text. A `j` typed into
  the code field or into a feed's title is a `j`."
  [^js e]
  (let [t   (.-target e)
        tag (some-> t .-tagName str/lower-case)]
    (or (contains? #{"input" "textarea" "select"} tag)
        (true? (some-> t .-isContentEditable)))))

(defn- plain?
  "No modifier held. `ctrl-l` and `cmd-l` belong to the browser, and taking
  them would be taking the address bar away."
  [^js e]
  (not (or (.-altKey e) (.-ctrlKey e) (.-metaKey e) (.-shiftKey e))))

;;; ------------------------------------------------------------- the article

(defn- scroll! [by]
  (when-let [el (state/read-box)]
    (.scrollBy el #js {:top by :behavior "auto"})
    true))

;;; --------------------------------------------------------------- the lists

(defn- list-element
  "Which list the keys are walking: the one the focus is in, otherwise the
  articles. A wide window has two of them on the screen at once, and the
  answer to \"which one\" is the one somebody is already in - which is what
  the tab key is for."
  []
  (let [active (.-activeElement js/document)]
    (or (some-> active (.closest "[data-keys=\"list\"]"))
        (.querySelector js/document "[data-list=\"articles\"]")
        (.querySelector js/document "[data-keys=\"list\"]"))))

(defn- move!
  "One row along, as the focus. Nothing happens past either end: a list that
  wrapped around would tell somebody they were at the top when they were at
  the bottom."
  [step]
  (when-let [el (list-element)]
    (let [rows   (vec (array-seq (.querySelectorAll el ".leser-row")))
          active (.-activeElement js/document)
          i      (first (keep-indexed (fn [i r] (when (= r active) i)) rows))
          j      (cond i           (+ i step)
                       (neg? step) (dec (count rows))
                       :else       0)]
      (when-let [row (get rows (max 0 (min (dec (count rows)) j)))]
        (.focus row)
        (.scrollIntoView row #js {:block "nearest"})
        true))))

;;; ------------------------------------------------------------- the handler

(defn handle!
  "One key press, on the window rather than on an element - so the keys work
  while the list has the focus and while nothing has it.

  Read out of the atom instead of out of a closure, so this listener is
  installed once and never replaced: a listener per render is a listener
  leaked per render, and a binding somebody just changed is in force on the
  next press either way.

  What is pressed is only swallowed when it did something. A key this file
  has no use for is left for the browser, which has plenty."
  [^js e]
  (let [{:keys [article dialog page settings]} @state/!state]
    ;; Backspace is the way back, everywhere - out of the article, out of the
    ;; settings, out of the pane - which is why it is read before the guards
    ;; below let a page through. It is not configurable and it is not vim's:
    ;; it is the key the whole web used to go back with, and the one thing it
    ;; must never do is eat a character, which is what `typing?` is for. A
    ;; dialog keeps it, so a mistyped address is corrected rather than
    ;; navigated away from.
    (when (and (= "Backspace" (.-key e)) (nil? dialog)
               (not (typing? e)) (plain? e))
      (.preventDefault e)
      (state/back!))
    (when (and (nil? dialog) (nil? page) (not (typing? e)) (plain? e))
      (let [bs       (bindings settings)
            k        (.-key e)
            reading? (and (some? article) (some? (state/read-box)))
            ;; The arrows do what they did before this file existed, and are
            ;; not configurable: an arrow key that pointed somewhere else
            ;; would be a lie about which way the page moves.
            action   (or (first (keep (fn [[a _]] (when (= k (get bs a)) a)) actions))
                         (case k
                           "ArrowRight" :keys/next
                           "ArrowLeft"  :keys/previous
                           nil))
            did?     (case action
                       :keys/down     (if reading? (scroll! line) (move! 1))
                       :keys/up       (if reading? (scroll! (- line)) (move! -1))
                       :keys/next     (when article (state/next-article!) true)
                       :keys/previous (when article (state/previous-article!) true)
                       ;; Out of the article and back to the list, where the
                       ;; keys walk rows again. The same escape that closes
                       ;; everything else in a window.
                       (when (and reading? (= "Escape" k))
                         (state/close-article!)
                         true))]
        (when did? (.preventDefault e))))))
